/**
 * La barra di stato della sessione: compatta, e sempre nello stesso posto.
 *
 * **Compatta perche' non e' il lavoro.** Il lavoro e' il fotogramma: ogni riga
 * che questa barra si prende e' una riga in meno di immagine, e l'immagine e'
 * l'unica cosa su cui si decide. Per questo tutto sta su una fascia sola, con i
 * numeri a cifra tabellare — un numero che cambia larghezza fa ballare le voci
 * accanto, e chi lavora a memoria le cerca dove erano. Da GI-266 la fascia
 * sta **dentro l'intestazione della shell**, nella stessa riga del titolo e
 * dell'identita': una riga di cromo invece di due.
 *
 * **Ma sempre visibile, perche' e' la sicurezza.** Sessione degradata, decisioni
 * ancora in consegna, riconciliazione in corso: sono le cose che rendono un
 * verdetto inutile senza che l'operatore lo sappia. Nasconderle dietro un
 * pannello che si apre significa che non le legge nessuno.
 *
 * **Tono, glifo e parola insieme.** Uno stato affidato al solo colore, per uno
 * operatore su dodici, non esiste (§ 03).
 */
import { moment } from '../format'
import { useT } from '../i18n/LocaleProvider'
import {
  CHIUSA,
  CONTESA,
  DEGRADATO,
  ESAURITO,
  OFFLINE,
  RICONCILIAZIONE,
  type Tone,
} from './state'
import type { SessionPhase } from './useReviewSession'

export interface CockpitHeaderProps {
  /** Su quale commessa si sta decidendo. */
  /** Nome che l'operatore riconosce: l'identificatore esterno, non l'UUID. */
  jobLabel?: string
  /** Quando la sessione scade, per dirlo a parole invece che con l'identificativo. */
  expiresAt: string | null
  waiting: number
  decided: number
  /** Quante decisioni aspettano di partire. */
  unsent: number
  phase: SessionPhase
  degraded: boolean
  online: boolean
  reconciling: boolean
  resumed: boolean
  contended: boolean
  /** Vero quando il batch in mano e' finito. */
  exhausted: boolean
  onClose: () => void
  /** Apre la mappa dei tasti: lo stesso gesto del tasto «?». */
  onHelp: () => void
  /** Cio' che non e' passato: lo disegna `Faults`, come banda sotto la testa,
   *  non la fascia — che sta in una riga sola e non deve crescere. */
  alerts: { message: string; code: string | null }[]
}

/** Un gettone di stato: glifo, parola e tono, e mai uno dei tre da solo. */
function Chip({ tone }: { tone: Tone }): JSX.Element {
  return (
    <span className="cockpit-gettone" style={{ color: `var(--${tone.tone})` }}>
      <span className="glifo" aria-hidden="true">
        {tone.glyph}
      </span>
      {tone.label}
    </span>
  )
}

export function CockpitHeader({
  jobLabel,
  expiresAt,
  waiting,
  decided,
  unsent,
  phase,
  degraded,
  online,
  reconciling,
  resumed,
  contended,
  exhausted,
  onClose,
  onHelp,
}: Omit<CockpitHeaderProps, 'alerts'>): JSX.Element {
  const t = useT()
  return (
    <section className="cockpit-stato" aria-label={t('cockpit.sessionState')}>
      <h2 className="solo-lettori">{t('cockpit.sessionState')}</h2>

      {/*
        Su quale commessa. Non e' un dettaglio d'archivio: due schede aperte su
        due commesse si somigliano, e senza questa riga l'operatore decide
        sulla scheda sbagliata e se ne accorge dal giornale.

        La sessione non si scrive piu' qui (GI-237): «nessuna · sola
        osservazione» e l'identificativo erano gergo del protocollo, non cose
        che al supervisore servono per decidere. Perche' un tasto e' spento lo
        dice il pannello della decisione, accanto al tasto; l'identificativo
        resta nell'indirizzo per chi chiama l'assistenza.
      */}
      <p className="cockpit-contesto t-num">
        <span>
          {t('cockpit.job')} <code>{jobLabel ?? '…'}</code>
        </span>
      </p>

      <p className="cockpit-conti t-num">
        <span>{t('cockpit.waitingCount', { n: waiting })}</span>
        <span>{t('cockpit.decidedCount', { n: decided })}</span>
        {unsent > 0 && <span>{t('cockpit.toSend', { n: unsent })}</span>}
      </p>

      {/*
        Un annuncio solo, in un posto solo. Le regioni vive che si moltiplicano
        si coprono a vicenda: il lettore di schermo legge l'ultima e l'operatore
        perde quella che contava.
      */}
      <p className="cockpit-vivo" role="status" aria-live="polite">
        {phase === 'apertura' && t('cockpit.opening')}
        {phase === 'chiusa' && <Chip tone={CHIUSA} />}
        {!online && <Chip tone={OFFLINE} />}
        {online && degraded && <Chip tone={DEGRADATO} />}
        {reconciling && <Chip tone={RICONCILIAZIONE} />}
        {contended && <Chip tone={CONTESA} />}
        {exhausted && !contended && <Chip tone={ESAURITO} />}
        {resumed && phase === 'attiva' && (
          <span className="tenue">{t('cockpit.resumed')}</span>
        )}
      </p>

      {phase === 'attiva' && (
        <p className="cockpit-turno">
          <button
            type="button"
            className="cockpit-azione cockpit-azione-piccola"
            onClick={onClose}
            title={expiresAt === null ? undefined : t('cockpit.sessionOpen', { when: moment(expiresAt) })}
          >
            {t('cockpit.closeSession')}
          </button>
          {/* La scadenza sta nel titolo del bottone e nel nome accessibile: in
              una riga sola non c'e' posto per la frase intera. */}
          {expiresAt !== null && (
            <span className="solo-lettori">{t('cockpit.sessionOpen', { when: moment(expiresAt) })}</span>
          )}
        </p>
      )}

      {/* La mappa dei tasti si apriva solo col tasto «?», e solo col fuoco
          dentro il banco: nessun segno diceva che esistesse (GI-281). Il
          comando porta il tasto scritto, cosi' insegna il gesto che sostituisce. */}
      <button type="button" className="cockpit-azione cockpit-azione-piccola cockpit-tasti" onClick={onHelp}>
        <kbd aria-hidden="true">?</kbd> {t('keys.help')}
      </button>
    </section>
  )
}

/**
 * Cio' che non e' passato, in un elenco solo: una banda sotto la testa, che
 * c'e' solo quando c'e' qualcosa da dire (GI-266).
 *
 * Una regione viva sola, e non una per guasto: le regioni vive che si
 * moltiplicano si coprono a vicenda, e il lettore di schermo legge l'ultima
 * mentre all'operatore serviva la prima. Persistenti, non avvisi che
 * svaniscono: sono l'unica traccia che un verdetto non e' stato registrato, e
 * chi lo ha dato e' gia' venti candidati piu' avanti. Il rifiuto si legge col
 * suo codice: la frase serve a chi lavora, il codice a chi deve capire perche'.
 */
export function Faults({ alerts }: { alerts: CockpitHeaderProps['alerts'] }): JSX.Element | null {
  if (alerts.length === 0) return null
  return (
    <ul role="alert" className="cockpit-guasti">
      {alerts.map((one, index) => (
        <li key={index}>
          {one.message}
          {one.code && <span className="tenue cockpit-codice"> {one.code}</span>}
        </li>
      ))}
    </ul>
  )
}
